import {
  deleteField,
  collection,
  getDocs,
  getDoc,
  getCountFromServer,
  query,
  where,
  orderBy,
  limit,
  doc,
  updateDoc,
  setDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { mentorAssignedCopy } from "./notificationCopy";
import { db, functions } from "./firebase";
import { getAllPrograms } from "../data/programs";

/**
 * Get all registered users.
 */
export async function getAllUsers() {
  const snap = await getDocs(collection(db, "users"));
  return snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
}

/**
 * Update a user's role: "student" | "mentor" | "admin".
 * Keeps mentorDirectory (uid + displayName only, ver sistema-unificado-roles-
 * acompanamiento.md) en sincronía: cualquiera con acceso a FRUTO puede leer ese
 * directorio para elegir acompañante, así que solo debe contener a quien de verdad
 * puede acompañar.
 */
export async function setUserRole(uid, role, displayName) {
  const updates = { role };
  if (role === "mentor" || role === "admin") updates.leadershipInterestAt = null;
  await updateDoc(doc(db, "users", uid), updates);
  if (role === "mentor" || role === "admin") {
    await ensureMentorDirectoryEntry(uid, displayName);
  } else {
    await deleteDoc(doc(db, "mentorDirectory", uid)).catch(() => {});
  }
}

/**
 * Permiso "Voz" (grabacion-audios-devocional.md, Fase B): quien graba el audio del devocional.
 * No es un rol — una voz puede ser también mentor —, es un campo aparte que solo escribe el
 * admin (firestore.rules). `languages`: subconjunto de ["es", "en", "nah"]; vacío lo quita.
 */
export async function setUserVoice(uid, languages) {
  await updateDoc(doc(db, "users", uid), {
    voice: languages.length ? { languages } : deleteField(),
  });
}

/**
 * Idempotent upsert into mentorDirectory. Used by setUserRole() on every role change
 * and by the admin panel's self-healing backfill for mentores preexistentes que nunca
 * pasaron por setUserRole desde que existe este mecanismo.
 */
export async function ensureMentorDirectoryEntry(uid, displayName) {
  await setDoc(doc(db, "mentorDirectory", uid), { uid, displayName: displayName || "" }, { merge: true });
}

/**
 * Returns users who completed FRUTO's last stage (Ramas) but aren't mentor/admin yet —
 * the visible "signal" for admin to consider promoting them (Decisión 3: la promoción
 * sigue siendo manual, esto solo la hace visible). No new indexes: reusa el mismo
 * patrón de query que getLeadershipPathUserIds, filtra client-side.
 */
export async function getFrutoGraduatesPendingPromotion() {
  const frutoProgramIds = getAllPrograms()
    .map((p) => p.id)
    .filter((id) => id.startsWith("fruto"));
  if (frutoProgramIds.length === 0) return [];
  const snap = await getDocs(
    query(collection(db, "enrollments"), where("programId", "in", frutoProgramIds))
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((e) => !!e.stageApprovals?.ramas);
}

/**
 * Assign a mentor to a student (sets mentorId on the student's profile).
 * Pass null to unassign. Clears mentorRequestedAt only when assigning a real mentor —
 * unassigning does not resurrect the original request (ver S2 "La puerta del acompañamiento").
 * Avisa al mentor por el bus (cierre-notificaciones-newsletter.md, "Avisos a admin/mentor")
 * solo al asignar, no al desasignar — no bloquea si falla.
 */
export async function assignMentor(studentUid, mentorUid, studentDisplayName) {
  const updates = { mentorId: mentorUid || null };
  if (mentorUid) updates.mentorRequestedAt = null;
  await updateDoc(doc(db, "users", studentUid), updates);

  if (mentorUid) {
    try {
      await addDoc(collection(db, "notifications"), {
        userId: mentorUid,
        type: "mentor_assigned",
        language: "es",
        payload: mentorAssignedCopy({ studentDisplayName }),
        status: "pending",
        readAt: null,
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.warn("[admin] no se pudo crear la notificación de mentor_assigned:", err?.code);
    }
  }
}

/**
 * Aggregate activity counts for the admin dashboard. Uses getCountFromServer
 * (aggregation queries) so it never reads note/response content — just counts,
 * consistent with personal notes being private by default.
 */
export async function getActivityStats() {
  const [enrollments, notes, sharedNotes] = await Promise.all([
    getCountFromServer(collection(db, "enrollments")),
    getCountFromServer(collection(db, "personalNotes")),
    getCountFromServer(query(collection(db, "personalNotes"), where("sharedWithMentor", "==", true))),
  ]);
  return {
    enrollments: enrollments.data().count,
    notes: notes.data().count,
    sharedNotes: sharedNotes.data().count,
  };
}

/**
 * Returns the set of user ids currently enrolled in any FRUTO program (leadership
 * path, e.g. "fruto-sensibilizacion"), so the admin panel can flag them separately
 * from general discipleship. See project-log/initiatives/paneles-discipulo-lider.md.
 */
export async function getLeadershipPathUserIds() {
  const frutoProgramIds = getAllPrograms()
    .map((p) => p.id)
    .filter((id) => id.startsWith("fruto"));
  if (frutoProgramIds.length === 0) return new Set();
  const snap = await getDocs(
    query(collection(db, "enrollments"), where("programId", "in", frutoProgramIds))
  );
  return new Set(snap.docs.map((d) => d.data().userId));
}

/**
 * Returns the set of user ids currently enrolled in a specific program — used by
 * the admin panel to show enroll/enrolled state for programs with admin-only
 * enrollment (e.g. "fruto-pastoral-8h", see paneles-discipulo-lider.md Fase 4).
 */
export async function getEnrolledUserIds(programId) {
  const snap = await getDocs(
    query(collection(db, "enrollments"), where("programId", "==", programId))
  );
  return new Set(snap.docs.map((d) => d.data().userId));
}

/**
 * Returns the set of user ids that COMPLETED a program (status === "completed") — usado
 * por el badge informativo de S15b ("✓ Sensibilización completada") junto al enroll de
 * fruto-pastoral-8h, ver project-log/sesiones/S15b-formacion-acompanante.md. No bloquea
 * el enroll, solo da contexto al admin.
 */
export async function getCompletedEnrollmentUserIds(programId) {
  const snap = await getDocs(
    query(collection(db, "enrollments"), where("programId", "==", programId))
  );
  return new Set(
    snap.docs.filter((d) => d.data().status === "completed").map((d) => d.data().userId)
  );
}

/**
 * Returns { [userId]: mentorId } for every enrollment in a program — used by admin
 * to prefill/reassign the activity-scoped FRUTO companion (mentorId per enrollment,
 * see project-log/ARCHIVE.md, "Sistema unificado de roles y acompañamiento", Decisión 1).
 */
export async function getProgramEnrollmentsMap(programId) {
  const snap = await getDocs(
    query(collection(db, "enrollments"), where("programId", "==", programId))
  );
  const map = {};
  snap.docs.forEach((d) => { map[d.data().userId] = d.data().mentorId || null; });
  return map;
}

/**
 * Botón "+ Nuevo aviso" en /admin/desarrollo/ — el sitio es estático, no hay forma
 * de escribir directo a project-log/INBOX.md desde la web en vivo, así que estos
 * envíos viven en Firestore y el monitor los muestra combinados con el INBOX.md
 * estático (marcados como nuevos, sin procesar todavía).
 */
export async function submitDevInboxEntry({ type, text, authorName, uid }) {
  await addDoc(collection(db, "devInbox"), {
    type,
    text,
    authorName: authorName || "",
    uid: uid || null,
    status: "nuevo",
    adminNote: null,
    createdAt: serverTimestamp(),
  });
}

export async function getDevInboxEntries() {
  const snap = await getDocs(query(collection(db, "devInbox"), orderBy("createdAt", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * "Mis reportes" — entradas de devInbox creadas por el propio usuario, para que pueda
 * ver si su reporte fue atendido (status + nota corta del admin). Requiere el campo
 * `uid` guardado desde submitDevInboxEntry() y la regla de lectura por dueño en
 * firestore.rules (bloque devInbox). Ordena en el cliente (no en la query) a propósito:
 * where("uid") + orderBy("createdAt") requeriría un índice compuesto nuevo en Firestore,
 * y el volumen esperado por usuario (reportes individuales, no miles) no lo justifica.
 */
export async function getMyDevInboxEntries(uid) {
  if (!uid) return [];
  const snap = await getDocs(query(collection(db, "devInbox"), where("uid", "==", uid)));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
}

/**
 * Panel admin (/admin/desarrollo/): actualiza el status (nuevo/visto/resuelto) y/o la
 * nota corta del admin en una entrada de devInbox. Mismo patrón que setUserRole().
 */
export async function updateDevInboxEntry(id, { status, adminNote }) {
  const updates = {};
  if (status !== undefined) updates.status = status;
  if (adminNote !== undefined) updates.adminNote = adminNote;
  await updateDoc(doc(db, "devInbox", id), updates);
}

/**
 * Send a push notification to one or more specific users (by uid).
 * Requires the caller to have the "admin" role (enforced server-side).
 */
export async function sendTargetedNotification(uids, title, body) {
  const sendTargeted = httpsCallable(functions, "sendTargetedNotification");
  const result = await sendTargeted({ uids, title, body });
  return result.data;
}

// ─── Newsletter (Fase 4 de diseno-sistema-email.md) ────────────────────────────
// El borrador (newsletterIssues) se lee/escribe directo desde el cliente — las reglas
// de Firestore ya lo restringen a isAdmin(). Solo el envío real pasa por Cloud
// Function (sendNewsletterIssue), porque necesita leer newsletterSubscribers,
// bloqueada a cliente.

/**
 * List all newsletter drafts/issues, most recently updated first.
 */
export async function getNewsletterIssues() {
  const snap = await getDocs(query(collection(db, "newsletterIssues"), orderBy("updatedAt", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Última corrida del devocional por correo (devotionalEmailLog, la escribe
 * sendDevotionalEmail.js). Se prefiere la última PROGRAMADA: una prueba manual siempre corre
 * en modo prueba, y mostrarla haría creer que el envío real está en prueba. Si no hay
 * ninguna programada todavía, se muestra la última que haya.
 */
export async function getLatestDevotionalEmailRun() {
  const snap = await getDocs(query(collection(db, "devotionalEmailLog"), orderBy("at", "desc"), limit(10)));
  const runs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return runs.find((r) => r.trigger === "schedule") || runs[0] || null;
}

export async function getNewsletterIssue(issueId) {
  const snap = await getDoc(doc(db, "newsletterIssues", issueId));
  return snap.exists() ? { id: issueId, ...snap.data() } : null;
}

/**
 * Create (issueId omitted) or update (issueId given) a newsletter draft.
 * `content`: { titleEs, bodyEs[], listItemsEs[], ctaUrlEs?, ctaTextEs?, titleEn, bodyEn[],
 * listItemsEn[], ctaUrlEn?, ctaTextEn?, imageUrl? }. CTA and imageUrl are optional (Fase 5 —
 * onMinistryStoryPublished.js sets them automatically; a hand-written draft may add them too).
 * imageUrl is a single field (not per-language) — mainImage in Contentful isn't localized.
 */
export async function saveNewsletterIssue(issueId, content, authorUid) {
  if (issueId) {
    await updateDoc(doc(db, "newsletterIssues", issueId), {
      ...content,
      updatedAt: serverTimestamp(),
    });
    return issueId;
  }
  const ref = await addDoc(collection(db, "newsletterIssues"), {
    ...content,
    status: "draft",
    createdBy: authorUid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    reviewedBy: null,
    reviewedAt: null,
    sentAt: null,
    sentCounts: null,
  });
  return ref.id;
}

/**
 * Mark the "Ya lo revisamos" checkbox — a process confirmation, not a technical gate
 * (decisión de Víctor 2026-08-14: no se fuerza que sea un admin distinto de quien
 * escribió el borrador).
 */
/**
 * Delete a draft (never a sent issue — the client-side check happens in the page, this is
 * just the Firestore call). Existe sobre todo para limpiar borradores de prueba.
 */
export async function deleteNewsletterIssue(issueId) {
  await deleteDoc(doc(db, "newsletterIssues", issueId));
}

export async function markNewsletterReviewed(issueId, reviewerUid) {
  await updateDoc(doc(db, "newsletterIssues", issueId), {
    reviewedBy: reviewerUid,
    reviewedAt: serverTimestamp(),
  });
}

export async function sendNewsletterTest(issueId, email, language) {
  const send = httpsCallable(functions, "sendNewsletterTest");
  const result = await send({ issueId, email, language });
  return result.data;
}

/**
 * `audience` (S16) es un OBJETO:
 *   { mode: 'all' }
 *   { mode: 'lang', lang: 'es' | 'en' }
 *   { mode: 'ids', ids: [docId, ...] }   ← los marcados en la pestaña Inscritos
 * La función sigue aceptando la forma vieja de string por retrocompatibilidad, pero desde
 * acá siempre se manda el objeto.
 */
export async function sendNewsletterIssue(issueId, audience) {
  const send = httpsCallable(functions, "sendNewsletterIssue");
  const result = await send({ issueId, audience });
  return result.data;
}

// ─── Newsletter: inscritos (S16) ───────────────────────────────────────────────
// A diferencia de newsletterIssues, la lista de suscriptores NO se puede leer desde el
// cliente: firestore.rules cierra newsletterSubscribers con `allow read, write: if false`
// a propósito (abrirla sería exponer la lista de correos). Las dos funciones de abajo son
// la única vía, y ninguna devuelve tokens.

/**
 * Lista completa de inscritos + conteos por estado, ordenada por alta más reciente.
 * Devuelve { subscribers: [{ id, email, language, status, source, displayName, uid,
 * createdAt, confirmedAt, unsubscribedAt }], counts: { confirmed, pending, unsubscribed } }.
 * Las fechas vienen como string ISO (no Timestamp): la función las serializa al salir.
 */
export async function listNewsletterSubscribers() {
  const list = httpsCallable(functions, "listNewsletterSubscribers");
  const result = await list();
  return result.data;
}

/**
 * Invita a alguien al boletín. Entra como 'pending' y solo pasa a 'confirmed' cuando la
 * persona hace clic en el correo — una invitación de admin no salta la confirmación.
 * `inviterName` es el nombre de quien ENVÍA la invitación (distinto de `displayName`, el
 * de quien la recibe) — si se omite, la Function cae al displayName de la cuenta admin y,
 * si tampoco existe, al nombre genérico del equipo.
 * Devuelve { success, alreadyExists? } (alreadyExists = ya estaba confirmado, no se mandó
 * ningún correo).
 */
export async function inviteToNewsletter({ email, language, displayName, inviterName }) {
  const invite = httpsCallable(functions, "inviteToNewsletter");
  const result = await invite({ email, language, displayName, inviterName });
  return result.data;
}

/**
 * Acción sobre un inscrito desde el panel: 'inviteDevotional' | 'resendConfirmation' |
 * 'unsubscribe' | 'delete' (ver functions/src/newsletter/adminSubscriberAction.js).
 */
export async function subscriberAction(id, action) {
  const run = httpsCallable(functions, "adminSubscriberAction");
  const result = await run({ id, action });
  return result.data;
}

/**
 * Asigna el audio de un devocional (asset en Contentful + entrada actualizada, sin publicar).
 * Corre en una Cloud Function: el token de gestión de Contentful nunca llega al navegador.
 * Devuelve { success, entryId }.
 */
export async function updateDevotionalAudio(date, langCode, audioUrl) {
  const update = httpsCallable(functions, "updateDevotionalAudio");
  const result = await update({ date, langCode, audioUrl });
  return result.data;
}

/**
 * Publica varias entradas de devocional de una vez (un solo build del sitio).
 * Devuelve { published, failed }.
 */
export async function publishDevotionalEntries(entryIds) {
  const publish = httpsCallable(functions, "publishDevotionalEntries");
  const result = await publish({ entryIds });
  return result.data;
}
